"use client";

// FoldSheet — one of netti's project folds, opened (MOVING-IN-PLAN.md §15).
//
// John, 2026-10-24: "each fold that gets written needs to have that same sort of
// opening, maybe within a modal: an interactive experience of all the things that
// got updated … what the ai said, if there was something that you said verbatim
// that made its way through, and accessing the transcripts from it in a very
// easy-to-scan-through way, in a code box … nothing overwhelming."
//
// THE sheet, in three quiet parts: what it read (each capture: the person's own
// words first when it carried any, then the ai's note, each in THE code block),
// what it changed (each page, what it gained and lost line by line, a new page
// said so), and the things it touched. A page nothing has written since can be
// put back as it was before the fold (asked twice, a save of its own).

import { useEffect, useId, useRef, useState } from "react";
import EtherModal from "../ether/EtherModal";
import CopyBlock from "../CopyBlock";
import { diffCounts, lineDiff } from "../../lib/intake/lineDiff";
import { whenWords } from "./MovedIn";
import { sourceOf } from "../activity/sources";
import "./fold-sheet.css";

interface Fold {
  project: { id: string; name: string; slug: string };
  commit: { hash: string; at: string; summary: string | null; description: string | null; kind?: "fold" | "tidy" };
  captures: Array<{ hash: string; note: string; words: string[]; client: string | null; at: string | null; kept: boolean }>;
  pages: Array<{ name: string; before: string | null; after: string | null; created: boolean; beforeGone?: boolean; changedSince: boolean }>;
  nodes: string[];
}

function Page({ page, project, commit, restored, onPutBack, kind = "fold" }: { page: Fold["pages"][number]; project: string; commit: string; restored: boolean; onPutBack: () => void; kind?: "fold" | "tidy" }) {
  const [open, setOpen] = useState(false);
  const [twice, setTwice] = useState(false);
  const [said, setSaid] = useState("");
  // the sheet's own put-back is told on the page, whatever the re-read says of it
  const told = said || (restored ? "put back as it was. the page keeps this in its history." : "");
  const d = page.before != null && page.after != null ? lineDiff(page.before, page.after) : null;
  const c = d ? diffCounts(d) : null;
  const putBack = async () => {
    if (!twice) return setTwice(true);
    try {
      const r = await fetch("/api/fold", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ project, commit, page: page.name }) });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; why?: string };
      setSaid(j.ok ? "put back as it was. the page keeps this in its history." : j.why ?? "that did not go through. please try again.");
      if (j.ok) onPutBack();
    } catch {
      setSaid("the connection dropped. nothing changed.");
    } finally {
      setTwice(false);
    }
  };
  return (
    <li className="fv-page">
      <div className="fv-page__head">
        <span className="fv-page__name">{page.name.replace(/_/g, " ")}</span>
        <span className="fv-page__meta">
          {page.created ? "a new page" : c ? `${c.added} lines in · ${c.removed} out` : page.beforeGone ? "changed · the page before is no longer kept" : "changed"}
          {restored ? " · put back" : page.changedSince ? " · changed again since" : ""}
        </span>
        <button type="button" className="quiet-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? "fold it away" : "see what changed"}
        </button>
      </div>
      {open && (
        <>
          {d ? (
            <pre className="fv-diff" aria-label={`what changed in ${page.name}`}>
              {d.map((x, k) =>
                x.t === "gap" ? (
                  <span key={k} className="fv-gap">{`… ${x.n} lines as they were`}</span>
                ) : (
                  <span key={k} className={`fv-line fv-line--${x.t}`}>
                    <span className="fv-mark" aria-hidden>{x.t === "add" ? "+" : x.t === "del" ? "−" : " "}</span>
                    {x.line || " "}
                  </span>
                ),
              )}
            </pre>
          ) : page.after != null ? (
            <CopyBlock text={page.after} className="fv-box copy-block--soft" />
          ) : (
            <p className="cm-sub">its words could not be read just now.</p>
          )}
          {!page.created && !page.changedSince && page.before != null && (
            <p className="fv-back">
              <button type="button" className="quiet-link" onClick={() => void putBack()}>
                {twice ? `yes, put it back as it was before this ${kind}` : "put this page back as it was"}
              </button>
            </p>
          )}
          {told && <p className="cm-where" aria-live="polite"><span aria-hidden>↳</span> {told}</p>}
        </>
      )}
    </li>
  );
}

/** a map tidy (lib/intake/tidy), taken back whole from the feed: every change as it was, while nothing has written it since */
function TakeTidyBack({ project, commit, onBack }: { project: string; commit: string; onBack: () => void }) {
  const [twice, setTwice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState("");
  const press = async () => {
    if (!twice) return setTwice(true);
    setBusy(true);
    try {
      for (let i = 0; i < 3; i++) {
        try {
          const r = await fetch(`/api/projects/${project}/tidy`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ undo: commit }) });
          const j = (await r.json().catch(() => ({}))) as { ok?: boolean; put_back?: number; let_go?: number; left?: string[]; already?: boolean };
          if (j.ok && j.already) {
            setSaid("taken back. the map is as it was before the tidy.");
            onBack();
            return;
          }
          if (j.ok) {
            const did = (j.put_back ?? 0) + (j.let_go ?? 0);
            const l = (j.left ?? []).map((n) => n.replace(/_/g, " "));
            setSaid(!did ? "nothing was taken back: each change was written again after the tidy, so the map stays as it is now." : l.length ? `taken back, all but what changed since: ${l.join(", ")}.` : "taken back. the map is as it was before the tidy.");
            if (did) onBack();
            return;
          }
        } catch {
          /* tried again */
        }
        await new Promise((ok) => setTimeout(ok, 2_000 * (i + 1)));
      }
      setSaid("the map did not answer just now, so the tidy is still in place. please press again in a moment.");
    } finally {
      setBusy(false);
      setTwice(false);
    }
  };
  return (
    <section aria-label="take the tidy back">
      <p className="fv-back">
        <button type="button" className="quiet-link" disabled={busy} onClick={() => void press()}>
          {twice ? "yes, take the whole tidy back" : "take the whole tidy back"}
        </button>
      </p>
      {said && <p className="cm-where" aria-live="polite"><span aria-hidden>↳</span> {said}</p>}
    </section>
  );
}

/** a fold read that fails, tried again on its own */
const FOLD_READ_RETRIES = [2_000, 6_000, 15_000];

export function FoldSheet({ fold, onClose }: { fold: { project: string; commit: string } | null; onClose: () => void }) {
  const titleId = useId();
  const [read, setRead] = useState<Fold | "loading" | "missing">("loading");
  const [n, setN] = useState(0);
  // the pages this sheet put back, so the re-read that follows never loses its word on them
  const [restored, setRestored] = useState<{ key: string; names: string[] }>({ key: "", names: [] });
  const shownKey = useRef("");
  const key = fold ? `${fold.project}:${fold.commit}` : "";
  useEffect(() => {
    if (!fold) return;
    let alive = true;
    // another fold: never the last one's pages while this one is read. the same fold read again keeps its pages on
    // screen until the answer is in
    if (shownKey.current !== `${fold.project}:${fold.commit}`) {
      shownKey.current = `${fold.project}:${fold.commit}`;
      setRead("loading");
    }
    // a read that fails is tried again on its own; only a fold that is truly not there (404) says so
    let timer: ReturnType<typeof setTimeout> | null = null;
    const read = (tries: number) =>
      void fetch(`/api/fold?project=${encodeURIComponent(fold.project)}&commit=${encodeURIComponent(fold.commit)}`, { cache: "no-store" })
        .then(async (r) => {
          if (!alive) return;
          const j = (await r.json().catch(() => ({}))) as { fold?: Fold };
          if (r.ok && j.fold) return setRead(j.fold);
          if (r.status === 404 || tries >= FOLD_READ_RETRIES.length) return setRead((was) => (typeof was === "object" ? was : "missing"));
          timer = setTimeout(() => read(tries + 1), FOLD_READ_RETRIES[tries]);
        })
        .catch(() => {
          if (!alive) return;
          if (tries >= FOLD_READ_RETRIES.length) return setRead((was) => (typeof was === "object" ? was : "missing"));
          timer = setTimeout(() => read(tries + 1), FOLD_READ_RETRIES[tries]);
        });
    read(0);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [fold, n]);
  const f = typeof read === "object" ? read : null;
  return (
    <EtherModal
      open={!!fold}
      onClose={onClose}
      labelledBy={titleId}
      size="lg"
      height="tall"
      className="fv-sheet"
      nav={{ crumb: [{ label: f?.project.name ?? "a project", onClick: onClose }, { label: f?.commit.kind === "tidy" ? "a tidy" : "a fold" }] }}
      header={
        <>
          <h2 id={titleId} className="eth-title">{f ? (f.commit.kind === "tidy" ? `how netti tidied ${f.project.name}` : `what netti folded into ${f.project.name}`) : "what netti folded"}</h2>
          {f && <p className="eth-meta">{[whenWords(f.commit.at), `${f.captures.filter((c) => c.kept).length} read in`, `${f.pages.length} ${f.pages.length === 1 ? "page" : "pages"} changed`].filter(Boolean).join(" · ")}</p>}
        </>
      }
    >
      {read === "loading" ? (
        <div className="fs" aria-busy="true">
          <span className="skeleton-line" style={{ width: "66%" }} />
          <span className="skeleton-line" style={{ width: "48%" }} />
        </div>
      ) : read === "missing" ? (
        <p className="cm-sub">this fold is not here any more.</p>
      ) : (
        <div className="fs">
          {(read.commit.description || read.commit.summary) && <p className="cm-sub">{read.commit.description || read.commit.summary}</p>}
          <section aria-label="what it changed">
            <p className="fv-head">what it changed</p>
            {read.pages.length ? (
              <ul className="fv-pages">
                {read.pages.map((p) => (
                  <Page
                    key={p.name}
                    page={p}
                    project={read.project.id}
                    commit={read.commit.hash}
                    kind={read.commit.kind}
                    restored={restored.key === key && restored.names.includes(p.name)}
                    onPutBack={() => {
                      setRestored((r) => ({ key, names: [...(r.key === key ? r.names : []), p.name] }));
                      setN((k) => k + 1);
                    }}
                  />
                ))}
              </ul>
            ) : (
              <p className="cm-sub">no page changed: what it read was already here, or was set aside.</p>
            )}
            {read.nodes.length > 0 && <p className="fv-nodes">and it touched {read.nodes.slice(0, 8).map((x) => x.replace(/_/g, " ")).join(" · ")}{read.nodes.length > 8 ? ` · and ${read.nodes.length - 8} more` : ""}</p>}
          </section>
          {read.commit.kind === "tidy" && <TakeTidyBack project={read.project.id} commit={read.commit.hash} onBack={() => setN((k) => k + 1)} />}
          {read.commit.kind !== "tidy" && (
          <section aria-label="what it read">
            <p className="fv-head">what it read</p>
            <ul className="fv-caps">
              {read.captures.map((c) => (
                <li key={c.hash} className="fv-cap" data-off={!c.kept || undefined}>
                  <span className="fv-cap__meta">
                    {c.client ? `${sourceOf(c.client).label}'s note` : "a note"}
                    {c.at ? ` · ${whenWords(c.at)}` : ""}
                    {!c.kept ? " · set aside" : ""}
                  </span>
                  {c.words.map((w, i) => (
                    <div key={i} className="fv-cap__part">
                      <span className="fv-cap__who">your words</span>
                      <CopyBlock text={w} className="fv-box copy-block--soft" />
                    </div>
                  ))}
                  {c.note && <CopyBlock text={c.note} className="fv-box copy-block--soft" />}
                </li>
              ))}
            </ul>
          </section>
          )}
        </div>
      )}
    </EtherModal>
  );
}
